import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const PHASE_LABELS = { poule: "Poule", quart: "Quart de finale", demi: "Demi-finale", finale: "FINALE" };

export default function LiveScore() {
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [settings, setSettings] = useState({ mode: "matches", discipline: null });
  const [timer, setTimer] = useState(null);
  const [now, setNow] = useState(new Date());

  const load = useCallback(async () => {
    const [mr, tr, sr, tir] = await Promise.all([
      api.get("/matches"),
      api.get("/teams"),
      api.get("/live-settings"),
      api.get("/timer"),
    ]);
    setMatches(mr.data);
    setTeams(tr.data);
    setSettings(sr.data);
    setTimer(tir.data);
  }, []);

  useEffect(() => {
    load();
    const poll = setInterval(load, 5000);
    const clock = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(poll); clearInterval(clock); };
  }, [load]);

  const name = (id) => teams.find((t) => t.id === id)?.name || "—";

  let remaining = null;
  if (timer) {
    if (timer.running && timer.started_at) {
      remaining = Math.max(0, Math.round(timer.duration - (now.getTime() - new Date(timer.started_at).getTime()) / 1000));
    } else if (timer.paused_remaining !== null && timer.paused_remaining !== undefined) {
      remaining = timer.paused_remaining;
    }
  }
  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const live = matches.filter((m) => m.status === "live");
  const upcoming = matches.filter((m) => m.status === "upcoming").slice(0, 6);
  const finished = matches.filter((m) => m.status === "finished").slice(-6).reverse();

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="live-score-page">
      {/* Top bar */}
      <div className="flex items-center justify-between px-8 py-4 border-b-4 border-[#FF6B1A] bg-[#0F0700]">
        <p className="font-display text-3xl sm:text-4xl">
          URBAN <span className="text-[#FF6B1A]">FEST</span> 2026
        </p>
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-2 text-[#FF3B30] font-black uppercase tracking-widest text-sm">
            <span className="w-3 h-3 rounded-full bg-[#FF3B30] animate-ping-slow" /> Live
          </span>
          <span className="font-mono text-2xl sm:text-3xl text-[#FFB800]" data-testid="live-clock">
            {now.toLocaleTimeString("fr-FR")}
          </span>
        </div>
      </div>

      {/* Chrono */}
      {remaining !== null && (
        <div
          className={`flex flex-col items-center justify-center py-4 border-b-4 ${remaining === 0 ? "border-[#FF3B30] bg-[#2A0000]" : "border-[#FFB800] bg-[#120900]"}`}
          data-testid="live-timer"
        >
          {timer?.label && (
            <p className="text-xs sm:text-sm font-black uppercase tracking-[0.4em] text-white/60 mb-1">{timer.label}</p>
          )}
          <p
            className={`font-mono font-bold leading-none text-7xl sm:text-8xl ${
              remaining === 0 ? "text-[#FF3B30] animate-ping-slow" : remaining <= 60 ? "text-[#FF3B30]" : "text-[#FFB800]"
            }`}
            data-testid="live-timer-value"
          >
            {fmt(remaining)}
          </p>
          {!timer?.running && remaining > 0 && (
            <p className="text-xs font-black uppercase tracking-[0.4em] text-[#FF9F0A] mt-1 animate-ping-slow">Pause</p>
          )}
          {remaining === 0 && (
            <p className="text-sm font-black uppercase tracking-[0.4em] text-[#FF3B30] mt-1">Temps écoulé !</p>
          )}
        </div>
      )}

      {/* Content */}
      {settings.mode === "teams" ? (
        <TeamsShowcase teams={teams} discipline={settings.discipline} />
      ) : (
      <div className="flex-1 px-8 py-6">
        {live.length === 0 ? (
          <div className="h-full min-h-[40vh] flex flex-col items-center justify-center text-center">
            <p className="font-display text-5xl sm:text-7xl text-white/25">AUCUN MATCH EN COURS</p>
            <p className="text-white/40 uppercase tracking-widest mt-4">Prochain match bientôt — restez chauds !</p>
          </div>
        ) : (
          <div className={`grid gap-6 ${live.length === 1 ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"}`}>
            {live.map((m) => (
              <div key={m.id} className="border-4 border-[#FF6B1A] bg-[#150800] p-6 sm:p-10" data-testid={`live-match-${m.id}`}>
                <div className="flex items-center justify-between mb-6">
                  <span className="text-xs sm:text-sm font-black uppercase tracking-[0.3em] text-[#FFB800]">
                    {m.discipline === "basket" ? "🏀 Basket 3x3" : "⚽ Foot 5v5"} · {PHASE_LABELS[m.phase] || m.phase}{m.pool ? ` · Poule ${m.pool}` : ""}
                  </span>
                  <span className="flex items-center gap-2 text-[#FF3B30] font-black uppercase text-xs tracking-widest">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#FF3B30] animate-ping-slow" /> En direct
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
                  <p className="font-display text-4xl sm:text-6xl leading-none text-right truncate">{name(m.team_a_id)}</p>
                  <p className="font-display text-6xl sm:text-8xl text-[#FF6B1A] px-4 whitespace-nowrap">
                    {m.score_a} <span className="text-white/30">–</span> {m.score_b}
                  </p>
                  <p className="font-display text-4xl sm:text-6xl leading-none truncate">{name(m.team_b_id)}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Upcoming + finished */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          <MatchList title="À VENIR" accent="#FFB800" items={upcoming} name={name} showScore={false} testid="upcoming-list" />
          <MatchList title="DERNIERS RÉSULTATS" accent="#2EE59D" items={finished} name={name} showScore testid="finished-list" />
        </div>
      </div>
      )}

      {/* Bottom marquee */}
      <div className="border-t-4 border-[#FFB800] bg-[#0F0700] py-3 overflow-hidden">
        <div className="uf-ticker-track whitespace-nowrap text-[#FFB800] font-bold uppercase tracking-wider text-sm">
          <span>URBAN FEST 2026 ★ 29 & 30 AOÛT ★ BASKET 3X3 · GYMNASE RIVOLI ★ FOOT 5V5 · JEAN-YOLE ★ CONCERTS · DANSE · GRAFF ★ &nbsp;</span>
          <span aria-hidden="true">URBAN FEST 2026 ★ 29 & 30 AOÛT ★ BASKET 3X3 · GYMNASE RIVOLI ★ FOOT 5V5 · JEAN-YOLE ★ CONCERTS · DANSE · GRAFF ★ &nbsp;</span>
        </div>
      </div>
    </div>
  );
}

function TeamsShowcase({ teams, discipline }) {
  const approved = teams.filter((t) => t.status === "approved" && (!discipline || t.discipline === discipline));
  const groups = [
    { key: "basket", label: "🏀 BASKET 3X3", accent: "#FF6B1A" },
    { key: "foot", label: "⚽ FOOT 5V5", accent: "#FFB800" },
  ].filter((g) => !discipline || g.key === discipline);

  return (
    <div className="flex-1 px-8 py-6" data-testid="teams-showcase">
      <p className="font-display text-4xl sm:text-6xl mb-8 text-center">
        LES <span className="text-[#FF6B1A]">ÉQUIPES</span> DU TOURNOI
      </p>
      {approved.length === 0 ? (
        <p className="text-center text-white/30 font-display text-4xl py-16">ÉQUIPES BIENTÔT DÉVOILÉES…</p>
      ) : (
        groups.map((g) => {
          const list = approved.filter((t) => t.discipline === g.key);
          if (list.length === 0) return null;
          return (
            <div key={g.key} className="mb-10">
              <p className="font-display text-2xl sm:text-3xl mb-4" style={{ color: g.accent }}>
                {g.label} <span className="text-white/40 text-xl">— {list.length} équipe{list.length > 1 ? "s" : ""}</span>
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {list.map((t) => (
                  <div
                    key={t.id}
                    className="border-2 bg-[#120900] p-5 flex flex-col justify-between min-h-[110px]"
                    style={{ borderColor: g.accent + "66" }}
                    data-testid={`showcase-team-${t.id}`}
                  >
                    <p className="font-display text-2xl sm:text-3xl leading-none break-words">{t.name}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs uppercase tracking-widest text-white/50">{t.quartier}</span>
                      {t.pool && (
                        <span className="text-xs font-black px-2 py-0.5 rounded-full text-black" style={{ background: g.accent }}>
                          Poule {t.pool}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function MatchList({ title, accent, items, name, showScore, testid }) {
  return (
    <div className="border border-white/15 bg-[#0D0D0D]" data-testid={testid}>
      <p className="font-display text-2xl px-5 py-3 border-b border-white/10" style={{ color: accent }}>{title}</p>
      {items.length === 0 ? (
        <p className="p-5 text-white/30 text-sm uppercase tracking-widest">Rien pour le moment</p>
      ) : (
        <ul>
          {items.map((m) => (
            <li key={m.id} className="flex items-center gap-3 px-5 py-3 border-b border-white/5 last:border-0">
              <span className="text-[10px] font-black uppercase tracking-widest text-white/40 w-24 shrink-0">
                {m.discipline === "basket" ? "Basket" : "Foot"} · {PHASE_LABELS[m.phase]?.split(" ")[0] || m.phase}
              </span>
              <span className="flex-1 text-right font-bold truncate text-lg">{name(m.team_a_id)}</span>
              <span className="font-display text-2xl px-2 whitespace-nowrap" style={{ color: accent }}>
                {showScore ? `${m.score_a} - ${m.score_b}` : "VS"}
              </span>
              <span className="flex-1 font-bold truncate text-lg">{name(m.team_b_id)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
